/**
 * Pending Trip Service
 * Handles storage and retrieval of pending trips for unauthorized users
 * Uses IndexedDB for the image file and sessionStorage for metadata
 */

const DB_NAME = 'WantInSummerDB';
const DB_VERSION = 2;
const STORE_NAME = 'pendingTripFiles';
const SESSION_STORAGE_KEY = 'pendingTripData';

const initDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains('pendingPostFiles')) {
        db.createObjectStore('pendingPostFiles');
      }
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
  });
};

const fileToArrayBuffer = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
};

const arrayBufferToFile = (arrayBuffer, fileName, fileType) => {
  return new File([arrayBuffer], fileName, { type: fileType });
};

/**
 * Save pending trip data and optional image file
 * @param {Object} tripData - Trip form data
 * @param {File|null} imageFile - Optional image file
 */
export const savePendingTrip = async (tripData, imageFile = null) => {
  try {
    const db = await initDB();
    let imageKey = null;

    if (imageFile) {
      imageKey = `trip_image_${Date.now()}`;
      const arrayBuffer = await fileToArrayBuffer(imageFile);

      await new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.put({
          arrayBuffer,
          fileName: imageFile.name,
          fileType: imageFile.type,
          lastModified: imageFile.lastModified,
        }, imageKey);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    }

    const metadata = {
      tripData,
      imageKey,
      timestamp: Date.now(),
    };

    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(metadata));
    return { success: true };
  } catch (error) {
    console.error('Error saving pending trip:', error);
    throw new Error('Не удалось сохранить поездку. Попробуйте ещё раз.');
  }
};

/**
 * Get pending trip data and image file
 * @returns {Promise<{tripData: Object, imageFile: File|null} | null>}
 */
export const getPendingTrip = async () => {
  try {
    const metadataStr = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!metadataStr) return null;

    const metadata = JSON.parse(metadataStr);
    const { tripData, imageKey, timestamp } = metadata;

    const maxAge = 24 * 60 * 60 * 1000;
    if (Date.now() - timestamp > maxAge) {
      await clearPendingTrip();
      return null;
    }

    let imageFile = null;

    if (imageKey) {
      const db = await initDB();
      imageFile = await new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.get(imageKey);
        request.onsuccess = () => {
          const fileData = request.result;
          if (fileData) {
            resolve(arrayBufferToFile(fileData.arrayBuffer, fileData.fileName, fileData.fileType));
          } else {
            resolve(null);
          }
        };
        request.onerror = () => reject(request.error);
      });
    }

    return { tripData, imageFile };
  } catch (error) {
    console.error('Error retrieving pending trip:', error);
    return null;
  }
};

/**
 * Clear pending trip data and image
 */
export const clearPendingTrip = async () => {
  try {
    const metadataStr = sessionStorage.getItem(SESSION_STORAGE_KEY);
    sessionStorage.removeItem(SESSION_STORAGE_KEY);

    if (metadataStr) {
      try {
        const { imageKey } = JSON.parse(metadataStr);
        if (imageKey) {
          const db = await initDB();
          const transaction = db.transaction([STORE_NAME], 'readwrite');
          transaction.objectStore(STORE_NAME).delete(imageKey);
          await new Promise((resolve, reject) => {
            transaction.oncomplete = () => resolve();
            transaction.onerror = () => reject(transaction.error);
          });
        }
      } catch (parseError) {
        console.error('Error parsing metadata during clear:', parseError);
      }
    }
  } catch (error) {
    console.error('Error clearing pending trip:', error);
  }
};

/**
 * Check if pending trip exists
 * @returns {boolean}
 */
export const hasPendingTrip = () => {
  return !!sessionStorage.getItem(SESSION_STORAGE_KEY);
};
